'use client';

import Link from 'next/link';
import { MapPin, Phone, Mail } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { SITE_CONFIG } from '@/data/site-constants';
import { ContactForm } from '@/components/public/contact-form';

export function ContactContent({ isTeacher }: { isTeacher: boolean }) {
  const t = useTranslations('public.contact');

  return (
    <>
      {/* Header */}
      <section className="relative overflow-hidden bg-[var(--eifo-bg-light)] py-16 md:py-24">
        <div
          aria-hidden
          className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-full bg-eifo-accent/20 blur-3xl"
        />
        <div className="relative mx-auto max-w-3xl px-4 text-center">
          <h1 className="font-display text-4xl font-bold text-eifo-primary md:text-5xl">
            {isTeacher ? t('heroTitleTeacher') : t('heroTitle')}
          </h1>
          <p className="mt-4 text-lg text-gray-600">
            {isTeacher ? t('heroSubtitleTeacher') : t('heroSubtitle')}
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="bg-white py-16">
        <div className="mx-auto max-w-7xl px-4">
          <div className="grid gap-12 lg:grid-cols-3">
            {/* Form — left 2 cols */}
            <div className="lg:col-span-2">
              <ContactForm variant={isTeacher ? 'teacher' : 'parent'} />
            </div>

            {/* Contact info — right col */}
            <div>
              <div className="rounded-xl border bg-[var(--eifo-bg-light)] p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
                <h3 className="mb-4 text-lg font-semibold text-eifo-primary">{t('infoHeading')}</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-eifo-orange" />
                    <p className="text-sm text-gray-600">{SITE_CONFIG.address}</p>
                  </div>
                  {SITE_CONFIG.phones.map((p) => (
                    <div key={p.number} className="flex items-start gap-3">
                      <Phone className="mt-0.5 h-5 w-5 shrink-0 text-eifo-orange" />
                      <div className="text-sm text-gray-600">
                        <span className="text-xs text-gray-400">{p.label}: </span>
                        <a href={`tel:${p.number.replace(/\s/g, '')}`} className="hover:text-eifo-primary">
                          {p.number}
                        </a>
                      </div>
                    </div>
                  ))}
                  <div className="flex items-start gap-3">
                    <Mail className="mt-0.5 h-5 w-5 shrink-0 text-eifo-orange" />
                    <a
                      href={`mailto:${SITE_CONFIG.email}`}
                      className="text-sm text-gray-600 hover:text-eifo-primary"
                    >
                      {SITE_CONFIG.email}
                    </a>
                  </div>
                </div>
              </div>

              {isTeacher ? (
                <div className="mt-6 rounded-xl border p-6 text-center transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
                  <p className="mb-3 text-sm text-gray-600">{t('teacherCardText')}</p>
                  <a
                    href="mailto:recruiting@eifo.us"
                    className="inline-block rounded-md bg-eifo-primary px-4 py-2 text-sm font-medium text-white hover:bg-eifo-primary/90"
                  >
                    {t('teacherCardButton')}
                  </a>
                </div>
              ) : (
                <>
                  <div className="mt-6 rounded-xl border p-6 text-center transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
                    <p className="mb-3 text-sm text-gray-600">{t('parentCardText')}</p>
                    <Link
                      href="/courses"
                      className="inline-block rounded-md bg-eifo-primary px-4 py-2 text-sm font-medium text-white hover:bg-eifo-primary/90"
                    >
                      {t('parentCardButton')}
                    </Link>
                  </div>

                  {/* Teacher off-ramp — an educator who lands here directly (not
                      via the Our Team page) can switch to the teacher inquiry form. */}
                  <div className="mt-6 rounded-xl border p-6 text-center transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
                    <p className="mb-3 text-sm text-gray-600">{t('teacherSwitchText')}</p>
                    <Link
                      href="/contact?inquiry=teacher"
                      className="inline-block rounded-md border border-eifo-primary px-4 py-2 text-sm font-medium text-eifo-primary hover:bg-eifo-primary hover:text-white"
                    >
                      {t('teacherSwitchButton')}
                    </Link>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
